import ExcelJS from 'exceljs';

// Convert 1-based column number to Excel column letter(s) (1=A, 27=AA, etc.)
const getColLetter = (colNum) => {
    let letter = '';
    let num = colNum;
    while (num > 0) {
        const mod = (num - 1) % 26;
        letter = String.fromCharCode(65 + mod) + letter;
        num = Math.floor((num - 1) / 26);
    }
    return letter;
};

const applySheetConfig = (ws, { columns = [], rows = [], headerStyle = {}, stripe = 'FFF0F7F5', freezeHeader = true, border = 'thick', instructions = [] }) => {
    const hFill = headerStyle.fill || 'FF24695C';
    const hFontColor = headerStyle.fontColor || 'FFFFFFFF';
    const hBold = headerStyle.bold !== false;
    const hFontSize = headerStyle.fontSize || 11;

    // Instructions rows above the table
    if (instructions.length) {
        instructions.forEach((text) => {
            const row = ws.addRow([text]);
            row.font = { size: 11, color: { argb: 'FF636E72' }, italic: true };
            ws.mergeCells(row.number, 1, row.number, Math.max(columns.length, 10));
        });
        ws.addRow([]);
    }
    const tableStartRow = instructions.length ? instructions.length + 2 : 1;

    const totalRows = rows.length + 1;
    const totalCols = columns.length;
    const bStyle = border || null;
    const bColor = bStyle ? { argb: headerStyle.borderColor || hFill } : null;

    const getOuterBorder = (rowIdx, colIdx) => {
        if (!bStyle) return {};
        const border = {};
        if (rowIdx === 0) border.top = { style: bStyle, color: bColor };
        if (rowIdx === totalRows - 1) border.bottom = { style: bStyle, color: bColor };
        if (colIdx === 0) border.left = { style: bStyle, color: bColor };
        if (colIdx === totalCols - 1) border.right = { style: bStyle, color: bColor };
        return border;
    };

    // Headers
    const headerRow = ws.addRow(columns.map(c => c.header));
    headerRow.font = { name: 'Arial Black', bold: hBold, size: hFontSize, color: { argb: hFontColor } };
    headerRow.height = hFontSize + 12;
    headerRow.eachCell((cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hFill } };
        cell.alignment = { vertical: 'middle', horizontal: columns[colNumber - 1]?.align || 'left' };
        const outer = getOuterBorder(0, colNumber - 1);
        cell.border = { ...outer, bottom: outer.bottom || { style: bStyle || 'thin', color: bColor || undefined } };
    });

    // Data rows
    rows.forEach((rowData, rowIdx) => {
        const row = ws.addRow(rowData);

        row.eachCell((cell, colNumber) => {
            const col = columns[colNumber - 1];
            if (col?.align) cell.alignment = { horizontal: col.align };
            cell.border = getOuterBorder(rowIdx + 1, colNumber - 1);
        });

        if (stripe && rowIdx % 2 === 1) {
            row.eachCell((cell) => {
                if (!columns[cell.col - 1]?.highlight) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stripe } };
                }
            });
        }

        columns.forEach((col, colIdx) => {
            if (col.highlight) {
                const cell = row.getCell(colIdx + 1);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: col.highlight } };
                if (col.highlightFont) {
                    cell.font = { color: { argb: col.highlightFont } };
                }
            }
        });
    });

    // Data validation — use a hidden sheet to avoid comma-delimiter issues in inline lists
    const validationCols = columns.reduce((acc, col, colIdx) => {
        if (col.validation?.length || col.rowValidation?.length) acc.push(colIdx);
        return acc;
    }, []);

    if (validationCols.length) {
        const wb = ws.workbook;
        let hiddenSheet = wb.getWorksheet('_ValidationData');
        if (!hiddenSheet) {
            hiddenSheet = wb.addWorksheet('_ValidationData');
            hiddenSheet.state = 'veryHidden';
        }

        // Track which hidden-sheet column to use next
        let hiddenColOffset = hiddenSheet.columnCount || 0;

        validationCols.forEach((colIdx) => {
            const col = columns[colIdx];
            const colLetter = String.fromCharCode(65 + colIdx);
            const hasRowValidation = col.rowValidation?.length > 0;

            if (hasRowValidation) {
                // Per-row validation: each row may have a different list, write each as a separate hidden column
                for (let i = tableStartRow + 1; i <= tableStartRow + rows.length; i++) {
                    const rowIdx = i - tableStartRow - 1;
                    const validationList = col.rowValidation[rowIdx] || col.validation;
                    if (!validationList?.length) continue;

                    hiddenColOffset++;
                    validationList.forEach((val, vIdx) => {
                        hiddenSheet.getCell(vIdx + 1, hiddenColOffset).value = val;
                    });

                    const hiddenColLetter = getColLetter(hiddenColOffset);
                    const rangeRef = `'_ValidationData'!$${hiddenColLetter}$1:$${hiddenColLetter}$${validationList.length}`;
                    ws.getCell(`${colLetter}${i}`).dataValidation = {
                        type: 'list',
                        formulae: [rangeRef],
                        showDropDown: true,
                    };
                }
            } else {
                // Shared validation: write once, reference for all rows
                hiddenColOffset++;
                const validationList = col.validation;
                validationList.forEach((val, vIdx) => {
                    hiddenSheet.getCell(vIdx + 1, hiddenColOffset).value = val;
                });

                const hiddenColLetter = getColLetter(hiddenColOffset);
                const rangeRef = `'_ValidationData'!$${hiddenColLetter}$1:$${hiddenColLetter}$${validationList.length}`;
                for (let i = tableStartRow + 1; i <= tableStartRow + rows.length; i++) {
                    ws.getCell(`${colLetter}${i}`).dataValidation = {
                        type: 'list',
                        formulae: [rangeRef],
                        showDropDown: true,
                    };
                }
            }
        });
    }

    // Column widths - auto-size based on content if no explicit width, or use explicit width as minimum
    columns.forEach((col, colIdx) => {
        const colNum = colIdx + 1;
        let maxLen = (col.header || '').length;
        rows.forEach((rowData) => {
            const val = String(rowData[colIdx] ?? '');
            if (val.length > maxLen) maxLen = val.length;
        });
        const autoWidth = Math.min(Math.max(maxLen + 3, 7), 60);
        ws.getColumn(colNum).width = col.width ? Math.max(col.width, autoWidth) : autoWidth;
    });

    if (freezeHeader) {
        ws.views = [{ state: 'frozen', ySplit: tableStartRow }];
    }
};

const downloadWorkbook = async (wb, fileName) => {
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const el = document.createElement('a');
    el.href = URL.createObjectURL(blob);
    el.download = fileName;
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
};

const buildExcelSheet = async ({ fileName = 'export.xlsx', sheetName = 'Sheet1', ...config }) => {
    const wb = new ExcelJS.Workbook();
    applySheetConfig(wb.addWorksheet(sheetName), config);
    await downloadWorkbook(wb, fileName);
};

const buildExcelWorkbook = async ({ fileName = 'export.xlsx', sheets = [] }) => {
    const wb = new ExcelJS.Workbook();
    sheets.forEach(({ sheetName = 'Sheet', ...config }) => {
        applySheetConfig(wb.addWorksheet(sheetName), config);
    });
    await downloadWorkbook(wb, fileName);
};

export { buildExcelSheet, buildExcelWorkbook };
